import { useState, useEffect, useCallback, useRef } from 'react';

import useSportsBonusModalPhase from './useSportsBonusModalPhase';
import { notify } from '../../../_helpers/notify';

import { validateBetCartLimits } from '../_helpers/betCartStakeLimits';
import { waitForPendingBetResolution } from '../_helpers/inplayDelayFlow';
import { extractBonusRecoveryGate } from '../_helpers/bonusRecovery';
import {
  recoverAfterSportsnewBetError,
  recoverAfterSportsnewBonusConfirmError,
} from '../_helpers/betErrorResync';
import { getSportsnewBetErrorMessage } from '../_helpers/sportsnewBetErrorMessage';
import {
  classifySportsnewPlacementResponse,
  SPORTSNEW_PLACEMENT_RESPONSE_DECISION,
} from '../_helpers/sportsnewPlacementAttemptPolicy';
import { normalizeSportsnewBetPlacementResponse } from '../_helpers/sportsnewBetPlacementResponse';
import { sportsnewPlacementAttemptRuntime } from '../_helpers/sportsnewPlacementAttemptRuntime';
import { fingerprintSportsnewPlacementCommand } from '../_helpers/sportsnewPlacementAttemptIdentity';
import { sportsnewPlacementUnresolvedError } from '../_helpers/sportsnewPlacementAttemptContract';

import { dispatchOptimisticSportsbookHistoryRow } from '../../../features/sportsbookHistory/optimistic/sportsbookHistoryOptimisticRows.optimistic';
import { isSportsbookUnifiedHistoryEnabled } from '../../../features/sportsbookHistory/policies/sportsbookHistory.policy';
import {
  clearPendingInplayBet,
  persistPendingInplayBet,
  readPendingInplayBet,
} from '../_helpers/sportsPageState';

import useSportsnewPreviewRecovery from '../_hooks/useSportsnewPreviewRecovery';
import { sportsnewPreviewPlacementGate } from '../_helpers/sportsnewPreviewPlacementGate';
import {
  buildSportsnewBetPlacementPreflightPayload,
  buildSportsnewBetSelectionPayload,
} from '../_helpers/betQuotePreviewPayload';
import { validateSportsnewPlacementPreflightQuote } from '../_helpers/sportsnewComboBonusPreview';
import { createSportsnewSubmissionGate } from '../_helpers/sportsnewSubmissionGate';

const fmt = n => Number(n).toLocaleString('ko-KR');
const SPORTSNEW_PLACEMENT_RESULT_UNKNOWN_MESSAGE = '베팅 요청의 처리 결과가 아직 확인되지 않았습니다. 새로 베팅하기 전에 베팅 내역을 확인하고, 새로고침 후 상태를 확인해주세요.';

const accountKeyOf = guard => `${String(guard?.userId ?? '')}:${String(guard?.generation ?? '')}`;
const retireNewUnadmittedPlacementAttempt = (attempt) => (
  attempt?.reused === false
  && !attempt.placementReceipt
  && sportsnewPlacementAttemptRuntime.retire(
    attempt.idempotencyKey,
    attempt.userId,
    attempt.challenge,
  )
);
const extractWalletPayload = (response) => {
  const payload = response?.data && typeof response.data === 'object' ? response.data : response;
  const walletSummary = payload?.walletSummary || payload?.wallet_summary;
  const balanceAfter = payload?.balanceAfter ?? payload?.balance_after;
  if (walletSummary) return walletSummary;
  if (balanceAfter != null) return { available_balance: balanceAfter };
  return null;
};
/** Owns receipt issuance, guarded placement, bonus recovery, and pending results. */
export default function useSportsnewBetPlacement({
  SportsnewService,
  activeCategory,
  activeDisplayScope,
  applyAccountWallet,
  betDetailService,
  betMode,
  betType,
  bettingLimits,
  bettingLimitsLoading,
  captureAccountGuard,
  capturePlacementAuthSessionCarrier,
  capturePlacementGuard,
  clearCart,
  currentAccountIdentity,
  currentAccountKey,
  displayedFinalOdds,
  effectiveMinimumStake,
  forceResyncOdds,
  hasAuthoritativeSportsnewQuote,
  isAccountGuardCurrent,
  isMountedRef,
  isPlacementGuardCurrent,
  maxStakeInfo,
  onMobileClose,
  presentationActive,
  previewKey,
  quoteError,
  quoteErrorCode,
  quoteLoading,
  recoverSelectedOddsForCart,
  refreshAvailableBalance,
  replaceSelectionsForType,
  selections,
  session,
  setBalanceLoading,
  setCurrentType,
  setResolvedBalanceRecord,
  setStake,
  stake,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [showBetSuccessModal, setShowBetSuccessModal] = useState(false);
  const closeBetSuccessModal = useCallback(() => setShowBetSuccessModal(false), []);
  const { open: openBonusPhase, beginConfirm, hide: hideBonusPhase, complete: completeBonusPhase, rearm: rearmBonusPhase, reset: resetBonusPhase } = useSportsBonusModalPhase();
  const [pendingBetState, setPendingBetState] = useState(null);
  const [showBonusRecoveryModal, setShowBonusRecoveryModal] = useState(false);
  const [bonusActionType, setBonusActionType] = useState('RECOVER');
  const [bonusGames, setBonusGames] = useState('sport');
  const [bonusRecoveryRequest, setBonusRecoveryRequest] = useState(null);
  const pendingBetFlowRef = useRef(null);
  const pendingBetCartHydratedIdRef = useRef(null);
  const submissionGateRef = useRef(null);
  if (!submissionGateRef.current) {
    submissionGateRef.current = createSportsnewSubmissionGate();
  }
  const recoverAfterBetRejection = useCallback((
    source,
    submittedSelections,
    accountGuard = captureAccountGuard(),
    options = {},
  ) => (
    recoverAfterSportsnewBetError({
      source,
      submittedSelections,
      displayScope: activeDisplayScope,
      recoverSelectedOddsForCart,
      forceResyncOdds,
      forceSelectedRecovery: options.forceSelectedRecovery === true,
      isCancelled: () => !isAccountGuardCurrent(accountGuard),
      recoveryOwnerKey: accountKeyOf(accountGuard),
    })
  ), [
    activeDisplayScope,
    captureAccountGuard,
    forceResyncOdds,
    isAccountGuardCurrent,
    recoverSelectedOddsForCart,
  ]);

  useSportsnewPreviewRecovery({
    accountKey: currentAccountKey,
    captureAccountGuard,
    isAccountGuardCurrent,
    previewKey,
    quoteError,
    recoverAfterBetRejection,
    selections,
  });

  const runPendingBetFlow = useCallback(async (
    pendingBet,
    accountGuard = captureAccountGuard(),
    { restoreCartFromSnapshot = false } = {},
  ) => {
    if (!pendingBet?.betId || !isAccountGuardCurrent(accountGuard)) return;
    const currentFlow = pendingBetFlowRef.current;
    if (
      currentFlow?.betId === pendingBet.betId
      && currentFlow?.accountGuard?.userId === accountGuard.userId
      && currentFlow?.accountGuard?.generation === accountGuard.generation
    ) return;

    const flowDescriptor = Object.freeze({ pendingBet, betId: pendingBet.betId, accountGuard });
    pendingBetFlowRef.current = flowDescriptor;
    const pendingBetKey = `${accountGuard.accountKey}:${String(pendingBet.betId)}`;
    const shouldRestoreCartFromSnapshot = restoreCartFromSnapshot
      && pendingBetCartHydratedIdRef.current !== pendingBetKey;
    // A persisted snapshot may hydrate a fresh mount once. The same bet ID
    // must never hydrate again after a timeout, route re-entry, or retry,
    // because the current cart may already contain newer WS lifecycle state.
    pendingBetCartHydratedIdRef.current = pendingBetKey;

    if (pendingBet.bettingCategory === 'LIVE') {
      setCurrentType('LIVE');
    }

    if (
      shouldRestoreCartFromSnapshot
      && Array.isArray(pendingBet.selections)
      && pendingBet.selections.length > 0
    ) {
      replaceSelectionsForType?.('LIVE', pendingBet.selections);
    }

    if (pendingBet.stake !== '' && pendingBet.stake != null) {
      setStake(String(pendingBet.stake));
    }

    setPendingBetState({
      betId: pendingBet.betId,
      secondsLeft: pendingBet.secondsLeft,
      accountGuard,
    });

    try {
      await refreshAvailableBalance(accountGuard);
      if (!isAccountGuardCurrent(accountGuard)) return;
      const resolvedBet = await waitForPendingBetResolution({
        SportsService: betDetailService,
        betId: pendingBet.betId,
        delaySeconds: pendingBet.secondsLeft,
        isCancelled: () => !isAccountGuardCurrent(accountGuard),
        onTick: (secondsLeft) => {
          if (!isAccountGuardCurrent(accountGuard)) return;
          setPendingBetState((prev) => (
            prev?.accountGuard === accountGuard ? { ...prev, secondsLeft } : prev
          ));
        },
      });

      if (!isAccountGuardCurrent(accountGuard)) return;

      setPendingBetState(null);
      await refreshAvailableBalance(accountGuard);

      if (!isAccountGuardCurrent(accountGuard)) return;

      if (!resolvedBet) {
        notify('인플레이 베팅 결과 확인이 지연되고 있습니다. 내 베팅에서 상태를 확인해주세요.', 'error');
        return;
      }

      clearPendingInplayBet({ userId: accountGuard.userId, betId: pendingBet.betId, backendKind: 'sportsnew' });

      if (resolvedBet.status === 'CONFIRMED') {
        replaceSelectionsForType?.('LIVE', []);
        setStake('');
        onMobileClose?.();
        setShowBetSuccessModal(true);
        return;
      }

      if (Array.isArray(pendingBet.selections) && pendingBet.selections.length > 0) {
        setCurrentType('LIVE');
        if (pendingBet.stake !== '' && pendingBet.stake != null) {
          setStake(String(pendingBet.stake));
        }
        await recoverAfterBetRejection(resolvedBet, pendingBet.selections, accountGuard, {
          // A delayed rejection is authoritative even when an older API shape
          // omits a structured rejection code. Refresh the submitted IDs once
          // so the cart keeps the current prices and lifecycle state.
          forceSelectedRecovery: true,
        });
        if (!isAccountGuardCurrent(accountGuard)) return;
      }

      notify(
        resolvedBet?.void_reason || '배당 변동 또는 마켓 상태 변경으로 인플레이 베팅이 거부되었습니다',
        'error'
      );
    } catch {
      if (!isAccountGuardCurrent(accountGuard)) return;
      setPendingBetState(null);
      notify('인플레이 베팅 상태 확인 중 오류가 발생했습니다. 내 베팅에서 상태를 확인해주세요.', 'error');
    } finally {
      if (pendingBetFlowRef.current === flowDescriptor) {
        pendingBetFlowRef.current = null;
      }
    }
  }, [
    betDetailService,
    captureAccountGuard,
    isAccountGuardCurrent,
    onMobileClose,
    recoverAfterBetRejection,
    refreshAvailableBalance,
    replaceSelectionsForType,
    setCurrentType,
    setStake,
  ]);

  useEffect(() => {
    if (activeCategory !== 'LIVE') return;

    const pendingBet = readPendingInplayBet({ userId: currentAccountIdentity.userId, backendKind: 'sportsnew' });
    if (!pendingBet) return;

    runPendingBetFlow(pendingBet, captureAccountGuard(), { restoreCartFromSnapshot: true });
  }, [activeCategory, captureAccountGuard, currentAccountIdentity.userId, currentAccountIdentity.accountKey, runPendingBetFlow]);

  useEffect(() => {
    setPendingBetState(null);
    setResolvedBalanceRecord(null);
    setBalanceLoading(false);
    setSubmitting(false);
    setShowBetSuccessModal(false);
    resetBonusPhase();
    setShowBonusRecoveryModal(false);
    setBonusRecoveryRequest(null);
  }, [currentAccountIdentity.accountKey, resetBonusPhase, setBalanceLoading, setResolvedBalanceRecord]);

  const closeBonusRecoveryModal = useCallback(() => {
    if (!presentationActive) return;
    const action = hideBonusPhase(bonusRecoveryRequest);
    if (action === 'stale') return;
    setShowBonusRecoveryModal(false);
    if (action === 'hide') return; // Native confirm→hide may not retire its continuation.
    if (!bonusRecoveryRequest?.placementRetry && !bonusRecoveryRequest?.receiptIssueRetry
      && isPlacementGuardCurrent(bonusRecoveryRequest?.attemptGuard, bonusRecoveryRequest?.payload)) {
      sportsnewPlacementAttemptRuntime.retire(
        bonusRecoveryRequest.payload.idempotencyKey,
        bonusRecoveryRequest.attemptGuard.userId,
        bonusRecoveryRequest.attemptGuard.challenge,
      );
    }
    setBonusRecoveryRequest(null);
  }, [bonusRecoveryRequest, hideBonusPhase, isPlacementGuardCurrent, presentationActive]);

  const openBonusRecoveryModal = useCallback((bonusGate, request) => {
    if (
      !bonusGate
      || !isMountedRef.current
      || !isPlacementGuardCurrent(request?.attemptGuard, request?.payload)
    ) return;
    setBonusGames(bonusGate.bonusGames);
    setBonusActionType(bonusGate.actionType);
    setBonusRecoveryRequest(openBonusPhase({
      ...request, actionType: bonusGate.actionType, placementRetry: false, receiptIssueRetry: false,
    }));
    setShowBonusRecoveryModal(true);
  }, [isMountedRef, isPlacementGuardCurrent, openBonusPhase]);

  const submitBetRequest = useCallback(async ({
    payload,
    submittedSelections,
    attemptGuard,
    authSessionCarrier,
  }) => {
    const requestStake = Number(payload?.stake ?? stake);
    // Crypto places directly; wallet refresh remains owned by mount/MAX/pending flows.
    if (!isPlacementGuardCurrent(attemptGuard, payload)) {
      return { attemptRetained: true, stale: true };
    }
    let res;
    try {
      res = await SportsnewService.placeBet(payload, { authSessionCarrier });
    } catch (error) {
      sportsnewPlacementAttemptRuntime.markUnknown(
        payload?.idempotencyKey,
        attemptGuard?.userId,
        attemptGuard?.challenge,
      );
      throw error;
    }

    const bonusGate = extractBonusRecoveryGate(res);
    const attemptTransition = sportsnewPlacementAttemptRuntime.recordResponse(
      payload?.idempotencyKey,
      res,
      { continuation: String(bonusGate?.actionType || '').toUpperCase() === 'RECOVER' },
      attemptGuard?.userId,
      attemptGuard?.challenge,
    );
    const attemptRetained = attemptTransition?.retained === true;

    if (bonusGate && attemptTransition?.persisted === false) {
      const error = new Error('Sportsnew placement continuation could not be persisted');
      error.code = 'SPORTSNEW_PLACEMENT_ATTEMPT_PERSISTENCE_UNAVAILABLE';
      throw error;
    }

    if (!isPlacementGuardCurrent(attemptGuard, payload)) return { attemptRetained, stale: true };

    if (bonusGate) {
      return { attemptRetained, bonusGate };
    }

    if (res?.status) {
      const walletPayload = extractWalletPayload(res);
      if (walletPayload && isPlacementGuardCurrent(attemptGuard, payload)) {
        applyAccountWallet(walletPayload, attemptGuard);
      }
      if (!isPlacementGuardCurrent(attemptGuard, payload)) return { attemptRetained, stale: true };
      const betResponse = normalizeSportsnewBetPlacementResponse(res);
      if (betMode === 'live' && betResponse.isPendingLive) {
        persistPendingInplayBet({
          betId: betResponse.betId,
          delaySeconds: betResponse.delaySeconds,
          delayExpiresAt: betResponse.delayExpiresAt,
          selections: submittedSelections,
          stake: requestStake,
          bettingCategory: activeCategory,
          userId: attemptGuard.userId,
          backendKind: 'sportsnew',
        });
        await runPendingBetFlow(
          readPendingInplayBet({ userId: attemptGuard.userId, backendKind: 'sportsnew' }) || {
            betId: betResponse.betId,
            bettingCategory: activeCategory,
            selections: submittedSelections,
            stake: String(requestStake),
            secondsLeft: betResponse.delaySeconds,
          },
          attemptGuard,
          { restoreCartFromSnapshot: false },
        );
        if (!isPlacementGuardCurrent(attemptGuard, payload)) {
          return { attemptRetained, stale: true };
        }
        return { attemptRetained };
      }

      if (betResponse.historyRow && isSportsbookUnifiedHistoryEnabled()) {
        dispatchOptimisticSportsbookHistoryRow(betResponse.historyRow);
      }
      if (!isPlacementGuardCurrent(attemptGuard, payload)) return { attemptRetained, stale: true };
      setShowBetSuccessModal(true);
      clearCart();
      return { attemptRetained };
    }

    const failMsg = classifySportsnewPlacementResponse(res) === SPORTSNEW_PLACEMENT_RESPONSE_DECISION.KEEP_UNKNOWN
      ? SPORTSNEW_PLACEMENT_RESULT_UNKNOWN_MESSAGE
      : getSportsnewBetErrorMessage(res, '베팅 실패');
    if (!isPlacementGuardCurrent(attemptGuard, payload)) return { attemptRetained, stale: true };
    notify(failMsg, 'error');
    await recoverAfterBetRejection(res, submittedSelections, attemptGuard);
    return { attemptRetained };
  }, [
    SportsnewService,
    activeCategory,
    applyAccountWallet,
    betMode,
    clearCart,
    isPlacementGuardCurrent,
    recoverAfterBetRejection,
    runPendingBetFlow,
        stake,
  ]);

  const handleBonusRecoveryConfirm = useCallback(async () => {
    if (!presentationActive || !bonusRecoveryRequest) return;
    const recoveryRequest = bonusRecoveryRequest;
    if (String(recoveryRequest.actionType || '').toUpperCase() === 'BLOCK'
      || !beginConfirm(recoveryRequest)) return;
    const authSessionCarrier = capturePlacementAuthSessionCarrier(recoveryRequest.attemptGuard);
    let activePayload = recoveryRequest.payload;
    let activeAttemptGuard = recoveryRequest.attemptGuard;
    const recoveryGuardCurrent = () => isPlacementGuardCurrent(
      activeAttemptGuard,
      activePayload,
    );
    if (!recoveryGuardCurrent()) {
      setShowBonusRecoveryModal(false);
      setBonusRecoveryRequest(null);
      return;
    }
    setShowBonusRecoveryModal(false);
    setSubmitting(true);
    let retainRecoveryRequest = false;
    let receiptIssued = false;
    let placementRequestStarted = false;
    try {
      // UNKNOWN retries keep the already-admitted immutable command and receipt.
      // Only a new server RECOVER gate authorizes replacing an attempt.
      if (!recoveryRequest.placementRetry) {
        const continuationCommand = Object.freeze({
          bet_type: recoveryRequest.payload?.bet_type,
          stake: recoveryRequest.payload?.stake,
          betting_category: recoveryRequest.payload?.betting_category,
          displayScope: recoveryRequest.payload?.displayScope,
          scope: recoveryRequest.payload?.scope,
          betMode: recoveryRequest.payload?.betMode,
          selections: recoveryRequest.payload?.selections,
          is_bonus_recover: true,
        });
        const retryingReceiptIssue = recoveryRequest.receiptIssueRetry === true;
        if (!retryingReceiptIssue) {
          sportsnewPlacementAttemptRuntime.authorizeContinuation(
            recoveryRequest.payload?.idempotencyKey,
            recoveryRequest.attemptGuard?.userId,
            recoveryRequest.attemptGuard?.challenge,
          );
          sportsnewPlacementAttemptRuntime.retire(
            recoveryRequest.payload?.idempotencyKey,
            recoveryRequest.attemptGuard?.userId,
            recoveryRequest.attemptGuard?.challenge,
          );
        }
        const nextAttempt = await sportsnewPlacementAttemptRuntime.acquire({
          userId: recoveryRequest.attemptGuard?.userId,
          accountGeneration: retryingReceiptIssue
            ? recoveryRequest.payload?.accountGeneration
            : recoveryRequest.attemptGuard?.generation,
          payload: continuationCommand,
        });
        if (
          retryingReceiptIssue
          && (
            nextAttempt.idempotencyKey !== recoveryRequest.payload?.idempotencyKey
            || nextAttempt.challenge !== recoveryRequest.payload?.challenge
          )
        ) throw new Error('SPORTSNEW_PLACEMENT_ATTEMPT_PERSISTENCE_UNAVAILABLE');
        activeAttemptGuard = capturePlacementGuard(nextAttempt, 'RECOVER');
        activePayload = Object.freeze({
          ...continuationCommand,
          idempotencyKey: nextAttempt.idempotencyKey,
          challenge: nextAttempt.challenge,
          accountGeneration: nextAttempt.accountGeneration,
          placementReceipt: nextAttempt.placementReceipt,
        });
        if (!recoveryGuardCurrent()) {
          retireNewUnadmittedPlacementAttempt(nextAttempt);
          return;
        }
        const nextReceiptResponse = await SportsnewService.issuePlacementReceipt({
          ...continuationCommand,
          idempotencyKey: nextAttempt.idempotencyKey,
          challenge: nextAttempt.challenge,
          accountGeneration: nextAttempt.accountGeneration,
        }, { authSessionCarrier });
        if (nextReceiptResponse?.localRequestRejected === true && !recoveryGuardCurrent()) {
          retireNewUnadmittedPlacementAttempt(nextAttempt);
          return;
        }
        if (!nextReceiptResponse?.status || !nextReceiptResponse?.data?.receipt) {
          throw nextReceiptResponse || new Error('SPORTSNEW_PLACEMENT_RECEIPT_UNAVAILABLE');
        }
        receiptIssued = true;
        if (!sportsnewPlacementAttemptRuntime.recordReceipt(
          nextAttempt.idempotencyKey,
          nextReceiptResponse.data,
          nextAttempt.userId,
          nextAttempt.challenge,
          nextAttempt.accountGeneration,
        )) throw new Error('SPORTSNEW_PLACEMENT_ATTEMPT_PERSISTENCE_UNAVAILABLE');
        const receiptedAttempt = sportsnewPlacementAttemptRuntime.current(nextAttempt.userId, {
          idempotencyKey: nextAttempt.idempotencyKey,
          challenge: nextAttempt.challenge,
          accountGeneration: nextAttempt.accountGeneration,
        });
        activeAttemptGuard = capturePlacementGuard(receiptedAttempt, 'RECOVER');
        activePayload = Object.freeze({
          ...continuationCommand,
          idempotencyKey: receiptedAttempt.idempotencyKey,
          challenge: receiptedAttempt.challenge,
          accountGeneration: receiptedAttempt.accountGeneration,
          placementReceipt: receiptedAttempt.placementReceipt,
        });
      }
      if (!recoveryGuardCurrent()) return;
      placementRequestStarted = true;
      const result = await sportsnewPlacementAttemptRuntime.runSingleFlight({
        idempotencyKey: activePayload.idempotencyKey,
        phase: 'RECOVER',
      }, () => submitBetRequest({
        payload: activePayload,
        submittedSelections: recoveryRequest.submittedSelections,
        attemptGuard: activeAttemptGuard,
        authSessionCarrier,
      }));
      retainRecoveryRequest = result?.attemptRetained === true;
      if (!recoveryGuardCurrent()) return;
      if (retainRecoveryRequest) {
        if (result?.bonusGate) {
          openBonusRecoveryModal(result.bonusGate, {
            ...recoveryRequest,
            payload: activePayload,
            attemptGuard: activeAttemptGuard,
          });
        } else {
          setBonusRecoveryRequest(openBonusPhase({
            ...recoveryRequest, payload: activePayload, attemptGuard: activeAttemptGuard,
            placementRetry: true, receiptIssueRetry: false,
          }));
        }
        setShowBonusRecoveryModal(true);
      }
    } catch (error) {
      if (!placementRequestStarted) {
        const receiptIssueDecision = sportsnewPlacementAttemptRuntime.recordReceiptIssueFailure(
          activePayload?.idempotencyKey,
          error,
          { receiptIssued, placementRequestStarted },
          activeAttemptGuard?.userId,
          activeAttemptGuard?.challenge,
        );
        retainRecoveryRequest = receiptIssueDecision?.retryReceiptIssue === true;
        if (!recoveryGuardCurrent()) return;
        if (retainRecoveryRequest) {
          setBonusRecoveryRequest(openBonusPhase({
            ...recoveryRequest,
            payload: activePayload,
            attemptGuard: activeAttemptGuard,
            receiptIssueRetry: true,
          }));
          setShowBonusRecoveryModal(true);
        }
        notify(getSportsnewBetErrorMessage(error, '베팅 요청 확인 오류'), 'error');
        return;
      }
      retainRecoveryRequest = sportsnewPlacementAttemptRuntime.markUnknown(
        activePayload?.idempotencyKey,
        activeAttemptGuard?.userId,
        activeAttemptGuard?.challenge,
      );
      if (!recoveryGuardCurrent()) return;
      const errMsg = SPORTSNEW_PLACEMENT_RESULT_UNKNOWN_MESSAGE;
      notify(errMsg, 'error');
      await recoverAfterSportsnewBonusConfirmError({
        source: error,
        bonusRecoveryRequest: {
          ...recoveryRequest,
          payload: activePayload,
          attemptGuard: activeAttemptGuard,
        },
        fallbackSelections: selections,
        displayScope: activeDisplayScope,
        recoverSelectedOddsForCart,
        forceResyncOdds,
        isCancelled: () => !recoveryGuardCurrent(),
        recoveryOwnerKey: accountKeyOf(activeAttemptGuard),
      });
      if (retainRecoveryRequest && recoveryGuardCurrent()) {
        setBonusRecoveryRequest(openBonusPhase({
          ...recoveryRequest, payload: activePayload, attemptGuard: activeAttemptGuard,
          placementRetry: true, receiptIssueRetry: false,
        }));
        setShowBonusRecoveryModal(true);
      }
    } finally {
      if (recoveryGuardCurrent()) {
        if (retainRecoveryRequest) {
          rearmBonusPhase(recoveryRequest);
        } else if (completeBonusPhase(recoveryRequest)) {
          setBonusRecoveryRequest(null);
        }
        setSubmitting(false);
      }
    }
  }, [
    SportsnewService,
    activeDisplayScope,
    bonusRecoveryRequest,
    beginConfirm, completeBonusPhase, openBonusPhase, rearmBonusPhase,
    capturePlacementAuthSessionCarrier,
    capturePlacementGuard,
    forceResyncOdds,
    isPlacementGuardCurrent,
    openBonusRecoveryModal,
    recoverSelectedOddsForCart,
    presentationActive,
    selections,
    submitBetRequest,
  ]);

  const handlePlace = async () => {
    const accountGuard = captureAccountGuard();
    if (!presentationActive || submissionGateRef.current.isLocked(accountGuard)) return;
    if (!session?.user) { notify('로그인이 필요합니다', 'error'); return; }
    const authSessionCarrier = capturePlacementAuthSessionCarrier(accountGuard);
    if (selections.length === 0) { notify('선택지를 추가하세요', 'error'); return; }
    // Never submit with an unresolved lock/odds-change — the user must apply
    // the cart-wide change resolution or remove the selection first.
    const unresolved = selections.find((s) => s.status && s.status !== 'active');
    if (unresolved) {
      const msg = unresolved.status === 'odds_changed'
        ? '배당이 변경된 항목이 있습니다. 변경사항을 수락하거나 삭제해주세요'
        : '베팅 불가능한 항목이 있습니다. 삭제 후 다시 시도해주세요';
      notify(msg, 'error');
      return;
    }
    const previewGate = sportsnewPreviewPlacementGate({
      betType,
      quoteLoading,
      hasAuthoritativeQuote: hasAuthoritativeSportsnewQuote,
      quoteError,
      quoteErrorCode,
    });
    if (!previewGate.ok) {
      notify(previewGate.message, 'error');
      return;
    }
    if (!stake || Number(stake) <= 0) { notify('베팅 금액을 입력하세요', 'error'); return; }
    if (!bettingLimitsLoading && Number(stake) < effectiveMinimumStake) {
      notify(`최소 베팅액은 ${fmt(effectiveMinimumStake)}원입니다`, 'error');
      return;
    }
    if (!bettingLimitsLoading) {
      const limitCheck = validateBetCartLimits({
        selections,
        stake,
        bettingLimits,
        displayedFinalOdds,
        maxStakeInfo,
      });
      if (!limitCheck.ok) {
        notify(limitCheck.message, 'error');
        return;
      }
    }
    const submittedStake = Number(stake);
    const submittedSelections = Object.freeze(
      selections.map((selection) => Object.freeze({ ...selection }))
    );
    const commandPayload = Object.freeze({
      bet_type: betType,
      stake: submittedStake,
      betting_category: activeCategory,
      displayScope: activeDisplayScope,
      scope: activeDisplayScope,
      betMode,
      selections: Object.freeze(submittedSelections.map(s => Object.freeze({
        match_id: s.match_id,
        ...buildSportsnewBetSelectionPayload(s),
      }))),
    });

    if (!isAccountGuardCurrent(accountGuard)) return;
    if (!submissionGateRef.current.tryAcquire(accountGuard)) return;
    setSubmitting(true);
    let idempotencyKey = '';
    let attemptGuard = null;
    let receiptIssued = false;
    let placementRequestStarted = false;
    try {
      // Cancelling an uncertain bonus continuation closes its prompt, not its
      // admitted intent. Do not create a new INITIAL key for that same bet.
      for (const pendingAttempt of sportsnewPlacementAttemptRuntime.list(accountGuard.userId)) {
        if (!isAccountGuardCurrent(accountGuard)) return;
        const continuationFingerprint = await fingerprintSportsnewPlacementCommand({
          userId: accountGuard.userId,
          accountGeneration: pendingAttempt.accountGeneration,
          payload: { ...commandPayload, is_bonus_recover: true },
        });
        if (!isAccountGuardCurrent(accountGuard)) return;
        if (pendingAttempt.fingerprint === continuationFingerprint) throw sportsnewPlacementUnresolvedError();
      }
      if (betType === 'COMBO') {
        const preflightResponse = await SportsnewService.previewBet(
          buildSportsnewBetPlacementPreflightPayload({
            selections: submittedSelections,
            betType,
            bettingCategory: activeCategory,
            displayScope: activeDisplayScope,
            betMode,
            stake: submittedStake,
          })
        );
        if (!isAccountGuardCurrent(accountGuard)) return;
        if (!preflightResponse?.status) {
          notify(getSportsnewBetErrorMessage(preflightResponse, '최종 베팅 조건을 확인하지 못했습니다'), 'error');
          await recoverAfterBetRejection(preflightResponse, submittedSelections, accountGuard);
          return;
        }
        const preflightGate = validateSportsnewPlacementPreflightQuote({
          quote: preflightResponse.data,
          stake: submittedStake,
        });
        if (!preflightGate.ok) {
          notify(preflightGate.message, 'error');
          return;
        }
      }
      if (!isAccountGuardCurrent(accountGuard)) return;
      const attempt = await sportsnewPlacementAttemptRuntime.acquire({
        userId: accountGuard.userId,
        accountGeneration: accountGuard.generation,
        payload: commandPayload,
      });
      idempotencyKey = attempt.idempotencyKey;
      attemptGuard = capturePlacementGuard(attempt, 'RECEIPT');
      let receiptAttempt = attempt;
      if (!isAccountGuardCurrent(accountGuard)) {
        retireNewUnadmittedPlacementAttempt(attempt);
        return;
      }
      if (!attempt.placementReceipt) {
        const receiptResponse = await SportsnewService.issuePlacementReceipt({
          ...commandPayload,
          idempotencyKey,
          challenge: attempt.challenge,
          accountGeneration: attempt.accountGeneration,
        }, { authSessionCarrier });
        if (receiptResponse?.localRequestRejected === true && !isAccountGuardCurrent(accountGuard)) {
          retireNewUnadmittedPlacementAttempt(attempt);
          return;
        }
        if (!receiptResponse?.status || !receiptResponse?.data?.receipt) {
          throw receiptResponse || new Error('SPORTSNEW_PLACEMENT_RECEIPT_UNAVAILABLE');
        }
        receiptIssued = true;
        const receiptSaved = sportsnewPlacementAttemptRuntime.recordReceipt(
          idempotencyKey,
          receiptResponse.data,
          attempt.userId,
          attempt.challenge,
          attempt.accountGeneration,
        );
        if (!receiptSaved) throw new Error('SPORTSNEW_PLACEMENT_ATTEMPT_PERSISTENCE_UNAVAILABLE');
        receiptAttempt = sportsnewPlacementAttemptRuntime.current(attempt.userId, {
          idempotencyKey: attempt.idempotencyKey,
          challenge: attempt.challenge,
          accountGeneration: attempt.accountGeneration,
        });
        if (!isAccountGuardCurrent(accountGuard)) return;
      }
      attemptGuard = capturePlacementGuard(receiptAttempt, 'INITIAL');
      const payload = Object.freeze({
        ...commandPayload,
        idempotencyKey,
        challenge: receiptAttempt.challenge,
        accountGeneration: receiptAttempt.accountGeneration,
        placementReceipt: receiptAttempt.placementReceipt,
      });
      if (!isPlacementGuardCurrent(attemptGuard, payload)) return;
      if (attempt.continuation) {
        setBonusGames('sport');
        setBonusActionType('RECOVER');
        setBonusRecoveryRequest(openBonusPhase({ payload, submittedSelections, actionType: 'RECOVER', attemptGuard: {
          ...attemptGuard,
          phase: 'RECOVER',
        } }));
        setShowBonusRecoveryModal(true);
        return;
      }
      placementRequestStarted = true;
      const result = await sportsnewPlacementAttemptRuntime.runSingleFlight({
        idempotencyKey,
        phase: 'INITIAL',
      }, () => submitBetRequest({
        payload,
        submittedSelections,
        attemptGuard,
        authSessionCarrier,
      }));
      if (result?.bonusGate && isPlacementGuardCurrent(attemptGuard, payload)) {
        openBonusRecoveryModal(result.bonusGate, { payload, submittedSelections, attemptGuard });
      }
    } catch (e) {
      if (idempotencyKey) {
        sportsnewPlacementAttemptRuntime.recordReceiptIssueFailure(
          idempotencyKey,
          e,
          { receiptIssued, placementRequestStarted },
          attemptGuard?.userId,
          attemptGuard?.challenge,
        );
      }
      if (!isAccountGuardCurrent(accountGuard)) return;
      const errMsg = placementRequestStarted
        ? SPORTSNEW_PLACEMENT_RESULT_UNKNOWN_MESSAGE
        : getSportsnewBetErrorMessage(e, '베팅 오류');
      notify(errMsg, 'error');
      await recoverAfterBetRejection(e, submittedSelections, attemptGuard || accountGuard);
    } finally {
      const released = submissionGateRef.current.release(accountGuard);
      if (released && isAccountGuardCurrent(accountGuard)) {
        setSubmitting(false);
      }
    }
  };

  return { showBetSuccessModal, closeBetSuccessModal, submitting, pendingBetState, showBonusRecoveryModal, bonusActionType, bonusGames, submissionGateRef, closeBonusRecoveryModal, handleBonusRecoveryConfirm, handlePlace };
}
